import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
interface WeatherData {
  temp: string;
  dew: string;
  humidity: string;
  windSpeed: string;
  windGust: string;
  windDir: string;
  civilDawn: string;
  civilDusk: string;
}
const EMPTY_DATA: WeatherData = {
  temp: '—',
  dew: '—',
  humidity: '—',
  windSpeed: '—',
  windGust: '—',
  windDir: '—',
  civilDawn: '—',
  civilDusk: '—'
};
// ─── Module-level cache (persists across mounts) ───
let cachedWeather: WeatherData | null = null;
let cachedQnh: string | null = null;
let lastWeatherFetch = 0;
let lastQnhFetch = 0;
let pollingStarted = false;
const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes
const RETRY_BASE_DELAY = 15000; // 15 seconds initial retry
const MAX_RETRY_DELAY = 120000; // 2 minutes max retry
// ─── Listeners for reactive updates ───
type Listener = () => void;
const listeners = new Set<Listener>();
function notifyListeners() {
  listeners.forEach(fn => fn());
}
// ─── XML Weather fetch & parse ───
async function fetchWeatherData(): Promise<WeatherData> {
  const response = await fetch('https://api.allorigins.win/raw?url=' + encodeURIComponent('http://93.99.194.142:2358/xml.xml'));
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const text = await response.text();
  const parser = new DOMParser();
  let xmlDoc = parser.parseFromString(text, 'text/xml');
  if (xmlDoc.querySelector('parsererror')) {
    const htmlDoc = parser.parseFromString(text, 'text/html');
    const body = htmlDoc.body?.textContent?.trim();
    if (body) {
      const xmlDoc2 = parser.parseFromString(body, 'text/xml');
      if (!xmlDoc2.querySelector('parsererror')) {
        xmlDoc = xmlDoc2;
      } else {
        throw new Error('XML parse error');
      }
    } else {
      throw new Error('XML parse error');
    }
  }
  return processXml(xmlDoc);
}
function processXml(xmlDoc: Document): WeatherData {
  const root = xmlDoc.documentElement;
  const sensors = xmlDoc.getElementsByTagName('sensor');
  const sensorMap: Record<string, string> = {};
  for (let i = 0; i < sensors.length; i++) {
    const sensor = sensors[i];
    const id = sensor.getElementsByTagName('id')[0]?.textContent?.trim() || '';
    const value = sensor.getElementsByTagName('value')[0]?.textContent?.trim() || '';
    if (id) sensorMap[id] = value;
  }
  const getById = (id: string): string | null => sensorMap[id] || null;
  const getByTag = (name: string): string | null => {
    const els = xmlDoc.getElementsByTagName(name);
    if (els.length > 0) {
      const text = els[0].textContent?.trim();
      if (text) return text;
      return els[0].getAttribute('value') || els[0].getAttribute('val') || null;
    }
    return null;
  };
  const windUnitRaw = getByTag('windunit') || getByTag('wunit') || getByTag('windunits') || getByTag('speedunit') || '';
  const windUnit = windUnitRaw.toLowerCase();
  const convertToKnots = (val: string | null): string => {
    if (!val) return '—';
    const num = parseFloat(val.replace(',', '.'));
    if (isNaN(num)) return val;
    let knots: number;
    if (windUnit.includes('km') || windUnit.includes('kmh') || windUnit.includes('kph')) {
      knots = num / 1.852;
    } else if (windUnit.includes('mph') || windUnit.includes('mi')) {
      knots = num / 1.151;
    } else if (windUnit.includes('m/s') || windUnit.includes('ms') || windUnit.includes('mps')) {
      knots = num * 1.944;
    } else {
      knots = num / 1.852;
    }
    return Math.round(knots).toString();
  };
  const formatTemp = (val: string | null): string => {
    if (!val) return '—';
    const num = parseFloat(val.replace(',', '.'));
    if (isNaN(num)) return val;
    return Math.round(num).toString();
  };
  const formatNum = (val: string | null): string => {
    if (!val) return '—';
    const num = parseFloat(val.replace(',', '.'));
    if (isNaN(num)) return val;
    return Math.round(num).toString();
  };
  const formatTime = (val: string | null): string => {
    if (!val || val === '—') return '—';
    const parts = val.split(':');
    if (parts.length !== 2) return val;
    const h = parts[0].trim().padStart(2, '0');
    const m = parts[1].trim().padStart(2, '0');
    return `${h}:${m} CET`;
  };
  return {
    temp: formatTemp(getById('1006')),
    dew: formatTemp(getById('1010')),
    humidity: formatNum(getById('1007')),
    windSpeed: convertToKnots(getById('1003')),
    windGust: convertToKnots(getById('1004')),
    windDir: formatNum(getById('1002')),
    civilDawn: formatTime(getByTag('civstart')),
    civilDusk: formatTime(getByTag('civend'))
  };
}
// ─── QNH fetch ───
async function fetchQnhData(): Promise<string> {
  const response = await fetch('https://api.allorigins.win/raw?url=' + encodeURIComponent('https://meteo.rlp.cz/xLKPR_metrep.htm'));
  if (!response.ok) throw new Error(`QNH HTTP ${response.status}`);
  const html = await response.text();
  const match = html.match(/Q(\d{4})/);
  if (match) return match[1];
  throw new Error('QNH not found');
}
// ─── Background polling with retry ───
async function pollWithRetry(fetchFn: () => Promise<void>, label: string, retryDelay = RETRY_BASE_DELAY) {
  try {
    await fetchFn();
  } catch (err) {
    const nextDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY);
    console.warn(`[METEO] ${label} failed, retrying in ${retryDelay / 1000}s...`, err);
    setTimeout(() => pollWithRetry(fetchFn, label, nextDelay), retryDelay);
  }
}
async function refreshWeather() {
  const data = await fetchWeatherData();
  cachedWeather = data;
  lastWeatherFetch = Date.now();
  console.log('[METEO] Weather cache updated:', data);
  notifyListeners();
}
async function refreshQnh() {
  const qnh = await fetchQnhData();
  cachedQnh = qnh;
  lastQnhFetch = Date.now();
  console.log('[METEO] QNH cache updated:', qnh);
  notifyListeners();
}
function startPolling() {
  if (pollingStarted) return;
  pollingStarted = true;
  // Initial fetch with retry
  pollWithRetry(refreshWeather, 'Weather');
  pollWithRetry(refreshQnh, 'QNH');
  // Poll every 5 minutes
  setInterval(() => {
    pollWithRetry(refreshWeather, 'Weather');
  }, POLL_INTERVAL);
  setInterval(() => {
    pollWithRetry(refreshQnh, 'QNH');
  }, POLL_INTERVAL);
}
// Start polling immediately when module loads
startPolling();
// ─── Component ───
export function Meteo() {
  const [data, setData] = useState<WeatherData>(cachedWeather || EMPTY_DATA);
  const [qnh, setQnh] = useState<string>(cachedQnh || '—');
  const [loading, setLoading] = useState(!cachedWeather);
  useEffect(() => {
    // Subscribe to cache updates
    const listener = () => {
      if (cachedWeather) {
        setData(cachedWeather);
        setLoading(false);
      }
      if (cachedQnh) {
        setQnh(cachedQnh);
      }
    };
    listeners.add(listener);
    // Immediately sync from cache (in case data arrived before mount)
    listener();
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.8,
    delay: 0.5
  }} className="w-full max-w-7xl mx-auto px-6 mb-12 relative z-20" data-id="element-141">
      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-12" data-id="element-142">
        {/* Label with QNH */}
        <div className="hidden lg:block shrink-0" data-id="element-143">
          <div className="border-l-2 border-[#38bdf8] pl-4 flex flex-col gap-1" data-id="element-144">
            <h3 className="text-[#38bdf8] font-thin text-2xl tracking-[0.2em] uppercase" data-id="element-145">
              Meteo
            </h3>
            <p className="text-sm" data-id="element-146">
              <span className="text-[#38bdf8] font-bold" data-id="element-147">QNH</span>
              <span className="text-[#38bdf8]/60 font-thin mx-1" data-id="element-148">LKPR</span>
              <span className={`text-[#38bdf8] font-bold font-mono ${qnh === '—' ? 'animate-pulse opacity-30' : ''}`} data-id="element-149">
                {qnh}
              </span>
            </p>
          </div>
        </div>

        {/* Data Grid */}
        <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-8 lg:gap-4" data-id="element-150">
          <WeatherItem label="Teplota" value={data.temp} unit="°C" loading={loading} data-id="element-151" />
          <WeatherItem label="Rosný bod" value={data.dew} unit="°C" loading={loading} data-id="element-152" />
          <WeatherItem label="Vlhkost" value={data.humidity} unit="%" loading={loading} data-id="element-153" />
          <WeatherItem label="Vítr" value={data.windSpeed} unit="kt" loading={loading} data-id="element-154" />
          <WeatherItem label="Nárazy" value={data.windGust} unit="kt" loading={loading} data-id="element-155" />
          <WeatherItem label="Směr" value={data.windDir} unit="°" loading={loading} data-id="element-156" />

          {/* Dawn/Dusk Group */}
          <div className="col-span-2 flex flex-col justify-center gap-1.5 border-t sm:border-t-0 sm:border-l border-white/10 pt-4 sm:pt-0 sm:pl-4 mt-2 sm:mt-0" data-id="element-157">
            <div className="flex justify-between items-baseline gap-2 text-xs text-white/40" data-id="element-158">
              <span className="uppercase tracking-wider whitespace-nowrap" data-id="element-159">
                Úsvit
              </span>
              <span className={`font-mono text-sm whitespace-nowrap ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-160">
                {data.civilDawn}
              </span>
            </div>
            <div className="flex justify-between items-baseline gap-2 text-xs text-white/40" data-id="element-161">
              <span className="uppercase tracking-wider whitespace-nowrap" data-id="element-162">
                Soumrak
              </span>
              <span className={`font-mono text-sm whitespace-nowrap ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-163">
                {data.civilDusk}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile QNH */}
      <div className="lg:hidden mt-4 text-center text-sm" data-id="element-164">
        <span className="text-[#38bdf8] font-bold" data-id="element-165">QNH</span>
        <span className="text-[#38bdf8]/60 font-thin mx-1" data-id="element-166">LKPR</span>
        <span className={`text-[#38bdf8] font-bold font-mono ${qnh === '—' ? 'animate-pulse opacity-30' : ''}`} data-id="element-167">
          {qnh}
        </span>
      </div>

      {!loading && !cachedWeather && <p className="text-white/20 text-[10px] text-center mt-4 font-mono" data-id="element-168">
          meteo nedostupné
        </p>}
    </motion.div>;
}
function WeatherItem({
  label,
  value,
  unit,
  loading
}: {
  label: string;
  value: string;
  unit: string;
  loading?: boolean;
}) {
  return <div className="flex flex-col" data-id="element-169">
      <span className="text-[10px] uppercase tracking-widest text-white/40 mb-1" data-id="element-170">
        {label}
      </span>
      <div className="flex items-baseline" data-id="element-171">
        <span className={`text-3xl font-extralight tracking-tighter ${loading ? 'text-[#38bdf8]/30 animate-pulse' : 'text-[#38bdf8]'}`} data-id="element-172">
          {value}
        </span>
        <span className="ml-1 text-sm text-[#38bdf8]/50 font-light" data-id="element-173">
          {unit}
        </span>
      </div>
    </div>;
}